import { useMemo } from "react";

import { CellContext, ColumnDef } from "@tanstack/react-table";
import { RejectedInvoiceTableDataDto } from "@/api/invoiceApi";
import { useTranslation } from "react-i18next";
import { TruncatedText } from "@/components/TruncatedText";
import { numericFormatter } from "react-number-format";
import { Flex, Text } from "@chakra-ui/react";
import { RejectedInvoiceModal } from "./RejectedInvoiceModal";

export function useColumns() {
    const { t } = useTranslation("claimsInvoicesOverview");
    return useMemo<ColumnDef<RejectedInvoiceTableDataDto>[]>(
        () => [
            {
                header: t("rejectedInvoicesTab.Table.rejectedDate"),
                accessorKey: "rejectedDate",
                cell: tableText("rejectedDate"),
                enableSorting: true,
                enableHiding: false,
                sortDescFirst: true
            },
            {
                header: t("rejectedInvoicesTab.Table.rejectedReason"),
                accessorKey: "rejectedReason",
                cell: rejectedCell,
                enableSorting: true,
                enableHiding: false,
                sortDescFirst: true
            },
            {
                header: t("rejectedInvoicesTab.Table.invoiceReference"),
                id: "invoiceReference",
                accessorKey: "invoiceReference",
                cell: tableText("invoiceReference"),
                enableSorting: true,
                enableHiding: false,
                sortDescFirst: true
            },
            {
                header: t("rejectedInvoicesTab.Table.invoiceDate"),
                accessorKey: "invoiceDate",
                enableSorting: true,
                enableHiding: false,
                sortDescFirst: true
            },
            {
                header: t("rejectedInvoicesTab.Table.claimsRef"),
                accessorKey: "claimsRef",
                cell: tableText("claimsRef"),
                enableSorting: true,
                enableHiding: false,
                sortDescFirst: true
            },
            {
                header: t("rejectedInvoicesTab.Table.totalInvoiceAmount"),
                accessorKey: "totalInvoiceAmount",
                cell: tableText("totalInvoiceAmount"),
                enableSorting: false,
                enableHiding: false,
                sortDescFirst: true
            }
        ],
        [t]
    );
}

const rejectedCell: ColumnDef<RejectedInvoiceTableDataDto>["cell"] = (
    ctx: CellContext<RejectedInvoiceTableDataDto, unknown>
) => {
    const { row } = ctx;

    return (
        <Flex h="fit-content" alignItems="center" gap={2} maxW="25rem">
            <Flex h="fit-content">
                <Text maxW="20rem" truncate>
                    {row.original.rejectedReason ?? ""}
                </Text>
            </Flex>
            {row.original.rejectedReason && (
                <RejectedInvoiceModal
                    targetedClaim={{
                        rejectedInvoiceMessage: row.original.rejectedReason,
                        claimsRef: row.original.claimsRef,
                        subClaimRef: row.original.subClaimRef
                    }}
                />
            )}
        </Flex>
    );
};

const DECIMAL_SCALE = 2;
const tableText =
    (tableId: string) =>
    ({ row }: CellContext<RejectedInvoiceTableDataDto, unknown>) => {
        if (tableId === "totalInvoiceAmount") {
            const value = row.original.totalInvoiceAmount;
            const currency = row.original.currency;
            const valueFormatted = numericFormatter(String(value), {
                thousandSeparator: ",",
                decimalSeparator: ".",
                decimalScale: DECIMAL_SCALE
            });
            return <TruncatedText text={`${valueFormatted} ${currency}`} />;
        }

        return <TruncatedText text={row.getValue(tableId)} />;
    };
